import { TicketAction, TicketActions } from './../actions/ticket.actions';
import { Ticket, TicketMedium } from '@ridango/core';
import { tassign } from 'tassign';

export interface TicketsState {
    docNums: any[];
    uids: any[];
    tickets: any;
    renew: any;
    relatedCardTickets: any;
    emailTickets: any;
    currentTicketUid: string;
    mediumTypes: any;
    vouchers: [];
    voucherSubTickets: [];
    selectedVoucher: any;
    renewableTicket: Ticket;
    refundTicket: Ticket;
}

export const TICKETS_INITIAL_STATE: TicketsState = {
    docNums: [],
    uids: [],
    tickets: {},
    renew: null,
    relatedCardTickets: {},
    emailTickets: [],
    currentTicketUid: '',
    mediumTypes: [
        new TicketMedium('U', 'LOCAL_LABEL_BUY_TICKETS_TO_ELRON_TRAVELCARD'),
        new TicketMedium('Q', 'LOCAL_LABEL_BUY_TICKETS_EMAIL')
    ],
    vouchers: [],
    voucherSubTickets: [],
    selectedVoucher: null,
    renewableTicket: null,
    refundTicket: null
};

export function ticketsReducer(lastState: TicketsState = TICKETS_INITIAL_STATE, action: TicketAction): TicketsState {
    // NB read this: https://redux.js.org/docs/recipes/reducers/UpdatingNormalizedData.html
    let ticket;
    switch (action.type) {
        case TicketActions.CREATE_TICKET:
            if (action.payload.doc_num) {
                return tassign(lastState, {
                    docNums: lastState.docNums.concat(action.payload.doc_num),
                    tickets: { ...lastState.tickets, [action.payload.doc_num]: action.payload }
                });
            }
            return tassign(lastState, {
                uids: lastState.uids.concat(action.payload.uid),
                tickets: { ...lastState.tickets, [action.payload.uid]: action.payload },
                currentTicketUid: action.payload.uid
            });
        case TicketActions.SET_TICKET_START_DATE:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        starting: action.payload.date
                    }
                }
            });
        case TicketActions.SET_TICKET_END_DATE:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        ending: action.payload.date
                    }
                }
            });
        case TicketActions.UNSET_TICKET_DATES:
            ticket = lastState.tickets[action.payload.uid];
            delete ticket.starting;
            delete ticket.ending;
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket
                    }
                }
            });

        case TicketActions.SET_TICKET_PRODUCT:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        product_id: action.payload.product ? action.payload.product.product_id : null,
                        product_object: Object.assign({}, action.payload.product),
                        product_price_object: action.payload.productPrice
                            ? Object.assign({}, action.payload.productPrice)
                            : null,
                        price:
                            action.payload.product && action.payload.product.price ? action.payload.product.price : null
                    }
                }
            });

        case TicketActions.SET_TICKET_ORIGIN_STOP:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        origin_stop_area_id: action.payload.stop ? action.payload.stop.id : null,
                        origin_stop_object: action.payload.stop
                    }
                }
            });

        case TicketActions.SET_TICKET_DESTINATION_STOP:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        destination_stop_area_id: action.payload.stop ? action.payload.stop.id : null,
                        destination_stop_object: action.payload.stop
                    }
                }
            });
        case TicketActions.CLEAR_TICKETS:
            return tassign(lastState, {
                docNums: [],
                uids: [],
                tickets: {},
                renew: null,
                relatedCardTickets: {},
                emailTickets: [],
                currentTicketUid: '',
                mediumTypes: [
                    new TicketMedium('U', 'LOCAL_LABEL_BUY_TICKETS_TO_ELRON_TRAVELCARD'),
                    new TicketMedium('Q', 'LOCAL_LABEL_BUY_TICKETS_EMAIL')
                ]
            });
        case TicketActions.REMOVE_TICKET:
            return tassign(lastState, {
                uids: lastState.uids.filter((u) => u !== action.payload.uid),
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {}
                }
            });
        case TicketActions.SET_RENEW_TICKET:
            return tassign(lastState, {
                renew: action.payload
            });
        case TicketActions.REMOVE_RENEW_TICKET:
            return tassign(lastState, {
                renew: null
            });

        case TicketActions.SET_AREA:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        area: Object.assign({}, action.payload.area)
                    }
                }
            });

        case TicketActions.SET_TRAVELLER_CATEGORY:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        travellerCategory: Object.assign({}, action.payload.travellerCategory)
                    }
                }
            });
        case TicketActions.SET_PRODUCT_CATEGORY:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        productCategory: Object.assign({}, action.payload.productCategory)
                    }
                }
            });
        case TicketActions.SET_MEDIUM:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        medium_object: Object.assign({}, action.payload.medium)
                    }
                }
            });

        case TicketActions.SET_RELATED_CARD:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        related_card_object: Object.assign({}, action.payload.card)
                    }
                }
            });

        case TicketActions.SET_TICKET_PRICE:
            return {
                ...lastState,
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...lastState.tickets[action.payload.uid],
                        price: +action.payload.price,
                        product_object: {
                            ...lastState.tickets[action.payload.uid].product_object,
                            price: +action.payload.price
                        }
                    }
                }
            };

        // tickets: {
        //     ...lastState.tickets,
        //     [ action.payload.uid ]: {
        //         price: action.payload.price
        //     }
        // }

        case TicketActions.SET_RELATED_CARD_TICKETS:
            return tassign(lastState, {
                relatedCardTickets: action.payload
            });

        case TicketActions.SET_TRAVEL_CARDS_TO_TICKET:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        travelCardsIds: action.payload.travelCardsIds
                    }
                }
            });

        case TicketActions.SET_MEDIUMS_TO_TICKET:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        mediumNums: action.payload.mediumNums
                    }
                }
            });

        case TicketActions.SET_EMAILS_TO_TICKET:
            ticket = lastState.tickets[action.payload.uid];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...ticket,
                        emails: action.payload.emails
                    }
                }
            });

        case TicketActions.SET_CORPORATE_PRICE:
            ticket = lastState.tickets[action.payload];
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload]: {
                        ...ticket,
                        price: ticket.product_object.price * ticket.travelCardsIds.length
                    }
                }
            });

        case TicketActions.TOGGLE_SELECTED_RELATED_CARD:
            if (!lastState.tickets[action.payload.uid].travelCardsIds) {
                return tassign(lastState, {
                    tickets: {
                        ...lastState.tickets,
                        [action.payload.uid]: {
                            ...lastState.tickets[action.payload.uid],
                            travelCardsIds: [action.payload.travelCardId]
                        }
                    }
                });
            }

            if (lastState.tickets[action.payload.uid].travelCardsIds.indexOf(action.payload.travelCardId) >= 0) {
                // travel card exists, remove
                return tassign(lastState, {
                    tickets: {
                        ...lastState.tickets,
                        [action.payload.uid]: {
                            ...lastState.tickets[action.payload.uid],
                            travelCardsIds: [
                                ...lastState.tickets[action.payload.uid].travelCardsIds.slice(
                                    0,
                                    lastState.tickets[action.payload.uid].travelCardsIds.indexOf(
                                        action.payload.travelCardId
                                    )
                                ),
                                ...lastState.tickets[action.payload.uid].travelCardsIds.slice(
                                    lastState.tickets[action.payload.uid].travelCardsIds.indexOf(
                                        action.payload.travelCardId
                                    ) + 1
                                )
                            ]
                        }
                    }
                });
            } else {
                // add travel card
                return tassign(lastState, {
                    tickets: {
                        ...lastState.tickets,
                        [action.payload.uid]: {
                            ...lastState.tickets[action.payload.uid],
                            travelCardsIds: [
                                ...lastState.tickets[action.payload.uid].travelCardsIds,
                                action.payload.travelCardId
                            ]
                        }
                    }
                });
            }

        case TicketActions.TOGGLE_ALL_RELATED_CARDS:
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...lastState.tickets[action.payload.uid],
                        travelCardsIds: action.payload.travelCardsIds
                    }
                }
            });

        case TicketActions.SET_EMAIL_TICKET:
            return tassign(lastState, {
                emailTickets: [...lastState.emailTickets, action.payload]
            });

        case TicketActions.CLEAR_EMAIL_TICKETS:
            return tassign(lastState, {
                emailTickets: []
            });

        case TicketActions.SET_VOUCHER_TICKET:
            return tassign(lastState, {
                vouchers: [...lastState.vouchers, action.payload]
            });
        case TicketActions.SET_VOUCHER_SUB_TICKET:
            return tassign(lastState, {
                voucherSubTickets: [...lastState.voucherSubTickets, action.payload]
            });

        case TicketActions.CLEAR_VOUCHERS:
            return tassign(lastState, {
                vouchers: [],
                voucherSubTickets: [],
                selectedVoucher: null
            });

        case TicketActions.SET_SELECTED_VOUCHER_TICKET:
            return tassign(lastState, {
                selectedVoucher: action.payload
            });

        case TicketActions.SET_AMOUNT_OF_TICKETS:
            return tassign(lastState, {
                tickets: {
                    ...lastState.tickets,
                    [action.payload.uid]: {
                        ...lastState.tickets[action.payload.uid],
                        amount: action.payload.amount
                    }
                }
            });
        case TicketActions.SET_AVAILABLE_TICKET_MEDIUM_TYPES:
            return tassign(lastState, {
                mediumTypes: action.payload
            });
        case TicketActions.SET_RENEWABLE_TICKET:
            return tassign(lastState, {
                renewableTicket: action.payload
            });
        case TicketActions.SET_REFUND_TICKET:
            return tassign(lastState, {
                refundTicket: action.payload
            });
    }

    // We don't care about any other actions right now.
    return lastState;
}
// return { count: lastState.count + 1 };
